import { createGlobalState } from '@vueuse/core'
import { useRepo } from 'pinia-orm'
import { useAppHider } from 'src/shared/composables/useHideApp/useAppHider'
import { ref, computed, watch } from 'vue'
import { QrCode } from '../QrCode'
import { BarcodeScanner } from '@capacitor-mlkit/barcode-scanning'
import { QrCodeScannerContract } from './QrCodeScannerContract'
import { mlKitQrScannerImplementation } from './mlKitQrScannerImplementation'
import { html5QrScannerImplementation } from './html5QrScannerImplementation'

export type OnScanCallback = (code: string) => void

let scanner: QrCodeScannerContract

export const useQrScannerGlobal = createGlobalState(() => {
  const appHider = useAppHider()
  const implementationName = ref<'html5' | 'mlkit'>()

  const result = ref()
  const withTorch = ref(false)

  BarcodeScanner.isSupported().then(result => {
    if (result.supported) {
      scanner = mlKitQrScannerImplementation
      implementationName.value = 'mlkit'
    } else {
      scanner = html5QrScannerImplementation
      implementationName.value = 'html5'
    }
  })

  watch(withTorch, async () => {
    const torchState = await BarcodeScanner?.isTorchEnabled()
    if (scanning.value) {
      if (torchState.enabled) BarcodeScanner?.disableTorch()
      if (!torchState.enabled) BarcodeScanner?.enableTorch()
    }
  })

  const qrCodeRepo = useRepo(QrCode)
  const qrCode = computed(() => {
    const foundById = qrCodeRepo.find(result.value)
    if (foundById) return foundById
    return qrCodeRepo.query().where('code', result.value).first()
  })

  const scanning = ref(false)

  function writeResult (raw: string) {
    const trimmed = raw.trim()
    result.value = trimmed
    return trimmed
  }

  async function scan (injectedRaw?: string) {
    if (injectedRaw !== undefined) {
      return writeResult(injectedRaw)
    }

    return new Promise<string>((resolve, reject) => {
      scanner.requestPermission().then(async () => {
        scanning.value = true
        appHider.hide()

        scanner.scan()
          .then(scanResult => {
            scanner.stop()
            resolve(writeResult(scanResult ?? ''))
          }).catch(error => {
            throw error
          }).finally(() => {
            scanning.value = false
            appHider.show()
          })

        setTimeout(() => {
          if (withTorch.value) {
            BarcodeScanner.enableTorch()
          }
        }, 250)
      }).catch((err) => {
        reject(err)
      })
    })
  }

  async function stopScan () {
    scanning.value = false
    appHider.show()
    scanner.stop()
  }

  return {
    scanning,
    scan,
    stopScan,
    result,
    qrCode,
    withTorch,
    implementationName,
  }
})
